import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { tenantApi, TenantSettingsResponse, TenantSettingsUpdateRequest, ContactInfo } from '../lib/tenant-api.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AggressivenessSlider from '../components/common/AggressivenessSlider.tsx';
import IntegrationsSection from '../components/IntegrationsSection.tsx';
import { SettingsIcon } from 'lucide-react';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import GuardrailSession from '../components/GuardrailSession.tsx';
import AppointmentSection from '../components/settings/AppointmentSection.tsx';
import AEOSection from '../components/settings/AEOSection.tsx';
import WidgetSection from '../components/settings/WidgetSection.tsx';
import ContactsSection from '../components/settings/ContactsSection.tsx';
import AdvancedSection from '../components/settings/AdvancedSection.tsx';
import SecuritySection from '../components/settings/SecuritySection.tsx';
import { usePermissions } from '../hooks/usePermissions';
import { ReadOnlyProvider } from '../contexts/ReadOnlyContext';
import { PageLoader, NewBadge } from '../components/common';
import { useNewFeatures } from '../hooks/useNewFeatures';

type SettingsTab = 'configuration' | 'integrations' | 'guardrails' | 'appointment' | 'aeo' | 'widget' | 'contacts' | 'advanced' | 'security';

// Tab config: maps tab keys to their display label and permission section key
const SETTINGS_TABS: { key: SettingsTab; label: string; sectionKey: string }[] = [
  { key: 'configuration', label: 'Configuration', sectionKey: 'configuration' },
  { key: 'integrations', label: 'Integrations', sectionKey: 'integrations' },
  { key: 'guardrails', label: 'Guardrails', sectionKey: 'guardrails' },
  { key: 'appointment', label: 'Appointment', sectionKey: 'appointment' },
  { key: 'aeo', label: 'AEO', sectionKey: 'aeo' },
  { key: 'widget', label: 'Widget', sectionKey: 'widget' },
  { key: 'contacts', label: 'Contacts', sectionKey: 'contacts' },
  { key: 'security', label: 'Security', sectionKey: 'security' },
  { key: 'advanced', label: 'Advanced', sectionKey: 'advanced' },
];

const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant, updateTenant, admin } = useTenantAuth();
  const isSuperAdmin = admin?.role?.toLowerCase() === 'super_admin';
  const [goal, setGoal] = useState('');
  const [aggressiveness, setAggressiveness] = useState(5.0);
  const [nonAnswerResponse, setNonAnswerResponse] = useState('');
  const [tenantType, setTenantType] = useState<'product' | 'service'>('product');
  const [amaOpeningStatements, setAmaOpeningStatements] = useState(true);
  const [activeTab, setActiveTab] = useState<SettingsTab>('configuration');
  const { getSettingsAccess } = usePermissions();
  const { hasNewForTab } = useNewFeatures();

  const { data: settings, isLoading, isError } = useQuery<TenantSettingsResponse>({
    queryKey: ['tenantSettings'],
    queryFn: tenantApi.getTenantSettings,
  });

  const { data: contactInfo } = useQuery<ContactInfo | null>({
    queryKey: ['contactInfo'],
    queryFn: tenantApi.getContactInfo,
  });

  const updateSettingsMutation = useMutation<TenantSettingsResponse, Error, TenantSettingsUpdateRequest>({
    mutationFn: tenantApi.updateTenantSettings,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
      setGoal(data.goal || '');
      setAggressiveness(data.aggressiveness || 5.0);
      setAmaOpeningStatements(data.ama_opening_statements_enabled !== false);

      // Update tenant information in context if tenant type changed
      if (tenant && data.tenant_type) {
        const updatedTenant = {
          ...tenant,
          tenant_type: (data.tenant_type?.toLowerCase() === 'service' ? 'service' : 'product') as 'product' | 'service',
          goal: data.goal,
          aggressiveness: data.aggressiveness
        };
        updateTenant(updatedTenant);
      }
    },
  });

  useEffect(() => {
    if (settings) {
      setGoal(settings.goal || '');
      setAggressiveness(settings.aggressiveness || 5.0);
      setTenantType(settings.tenant_type?.toLowerCase() === 'service' ? 'service' : 'product');
      setAmaOpeningStatements(settings.ama_opening_statements_enabled !== false);
    }
  }, [settings]);

  useEffect(() => {
    if (contactInfo === undefined) return;
    setNonAnswerResponse(contactInfo?.non_answer_response ?? '');
  }, [contactInfo]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const validTabs: SettingsTab[] = [
      'configuration',
      'integrations',
      'guardrails',
      'appointment',
      'aeo',
      'widget',
      'contacts',
      'advanced',
      'security',
    ];
    if (tab && validTabs.includes(tab as SettingsTab)) {
      if (tab === 'security' && isSuperAdmin) {
        setActiveTab('configuration');
        navigate('/settings?tab=configuration', { replace: true });
      } else {
        setActiveTab(tab as SettingsTab);
      }
    } else {
      setActiveTab('configuration');
    }
  }, [location.search, isSuperAdmin, navigate]);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    navigate(`/settings?tab=${tab}`, { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateSettingsMutation.mutateAsync({
        goal,
        aggressiveness,
        tenant_type: tenantType,
        ama_opening_statements_enabled: amaOpeningStatements,
      });
      await tenantApi.createOrUpdateContactInfo({
        non_answer_response: nonAnswerResponse.trim() ? nonAnswerResponse.trim() : null,
      });
      await queryClient.invalidateQueries({ queryKey: ['contactInfo'] });
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error(`Failed to update settings: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return <div className="p-6 text-red-500">Error loading settings.</div>;
  }

  const activeTabClassname = 'border-indigo-500 text-indigo-600';
  const inactiveTabClassname = 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300';
  const disabledTabClassname = 'border-transparent text-gray-300 cursor-not-allowed';

  // Get the active tab's section-level access for wrapping content
  const activeTabConfig = SETTINGS_TABS.find(t => t.key === activeTab);
  const activeTabAccess = activeTabConfig ? getSettingsAccess(activeTabConfig.sectionKey) : 'full';
  const isActiveTabReadOnly = activeTabAccess === 'read-only';

  const renderTabContent = () => {
    // If the active tab is disabled or hidden, don't render content
    if (activeTabAccess === 'disabled' || activeTabAccess === 'hidden') {
      return (
        <div className="text-center py-12 text-gray-400">
          <p>You don't have access to this section.</p>
        </div>
      );
    }

    const content = (() => {
      switch (activeTab) {
        case 'configuration':
          return (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
                  Conversation Goal
                </label>
                <textarea
                  id="goal"
                  rows={4}
                  className="mt-1 block w-full  border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="e.g., 'Help customers find the right product and complete a purchase.'"
                  disabled={updateSettingsMutation.isLoading}
                ></textarea>
                <p className="mt-2 text-sm text-gray-500">
                  Define the primary objective for your AI assistant's conversations.
                </p>
              </div>

              <div className="mb-6">
                <label htmlFor="tenant-type" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <select
                  id="tenant-type"
                  value={tenantType}
                  onChange={(e) => setTenantType(e.target.value as 'product' | 'service')}
                  className="mt-1 block w-full  border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  disabled={updateSettingsMutation.isLoading}
                >
                  <option value="product">Product Company</option>
                  <option value="service">Service Company</option>
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  This determines which AI prompts and tools your customers will experience.
                </p>
              </div>

              <div className="mb-6 flex items-start gap-3">
                <input
                  id="ama-opening-statements"
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={amaOpeningStatements}
                  onChange={(e) => setAmaOpeningStatements(e.target.checked)}
                  disabled={updateSettingsMutation.isLoading}
                />
                <div className="flex-1">
                  <label htmlFor="ama-opening-statements" className="block text-sm font-medium text-gray-700">
                    Opening statements in Ask Me Anything
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    When enabled, synthesized widget answers may start with a brief conversational opener (for example thanking the user). Turn off to ask the assistant to go straight to the answer. Stored primer answers are unchanged.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <label htmlFor="aggressiveness" className="block text-sm font-medium text-gray-700 mb-4">
                  Aggressiveness Level
                </label>
                <AggressivenessSlider
                  value={aggressiveness}
                  onChange={setAggressiveness}
                  disabled={updateSettingsMutation.isLoading}
                />
                <p className="mt-4 text-sm text-gray-500">
                  Adjust how assertively the AI pursues the conversation goal.
                  <span className="block mt-1">
                    <span className="text-green-600 font-semibold">0-3: Passive</span> (Informative, less direct)
                  </span>
                  <span className="block">
                    <span className="text-yellow-600 font-semibold">4-6: Balanced</span> (Engaging, moderately direct)
                  </span>
                  <span className="block">
                    <span className="text-red-600 font-semibold">7-10: Assertive</span> (Direct, conversion-focused)
                  </span>
                </p>
              </div>

              <div className="mb-8">
                <label htmlFor="non-answer-response" className="block text-sm font-medium text-gray-700 mb-2">
                  No-answer message (optional)
                </label>
                <textarea
                  id="non-answer-response"
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
                  value={nonAnswerResponse}
                  onChange={(e) => setNonAnswerResponse(e.target.value)}
                  placeholder="Shown when Ask Me Anything has no matching answer. Leave blank for the default message (and optional sales link from Contacts)."
                  disabled={updateSettingsMutation.isLoading}
                  maxLength={2000}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Optional. If empty, customers see the standard no-answer text. The contact form is still shown after this message.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
                <Link
                  to="/settings/advanced"
                  className="inline-flex items-center justify-center sm:justify-start text-sm text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <SettingsIcon className="h-4 w-4 mr-1" />
                  Advanced Prompt Management
                </Link>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={updateSettingsMutation.isLoading}
                >
                  {updateSettingsMutation.isLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          );
        case 'integrations':
          return (
            <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
              <IntegrationsSection />
            </GoogleOAuthProvider>
          );
        case 'guardrails':
          return <GuardrailSession />;
        case 'appointment':
          return <AppointmentSection />;
        case 'aeo':
          return <AEOSection />;
        case 'widget':
          return <WidgetSection />;
        case 'contacts':
          return <ContactsSection />;
        case 'security':
          return <SecuritySection />;
        case 'advanced':
          return <AdvancedSection />;
        default:
          return null;
      }
    })();

    // Wrap in ReadOnlyProvider to control API write access per-section
    return (
      <ReadOnlyProvider isReadOnly={isActiveTabReadOnly}>
        {content}
      </ReadOnlyProvider>
    );
  };

  return (
    <div className="mx-auto bg-white p-4 sm:p-8 shadow-md">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Tenant Settings</h1>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex overflow-x-auto gap-1 pb-px [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Tabs">
          {SETTINGS_TABS.map((tab) => {
            const access = getSettingsAccess(tab.sectionKey);

            // Super admins impersonating a tenant cannot change password from this UI
            if (tab.key === 'security' && isSuperAdmin) return null;

            // Hidden: don't render the tab at all
            if (access === 'hidden') return null;

            // Disabled: render greyed out, not clickable
            if (access === 'disabled') {
              return (
                <span
                  key={tab.key}
                  className={`${disabledTabClassname} whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm`}
                  title="You don't have access to this section"
                >
                  {tab.label}
                </span>
              );
            }

            // Full or read-only: render normally
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`${activeTab === tab.key
                  ? activeTabClassname
                  : inactiveTabClassname
                  } whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm inline-flex items-center`}
              >
                {tab.label}
                {hasNewForTab(tab.key) && <NewBadge />}
              </button>
            );
          })}
        </nav>
      </div>

      {renderTabContent()}
    </div>
  );
};

export default Settings;
